import React, { useState } from "react";
import { Shield, Scale, Save, Plus, Trash2, Mail, Calendar, Sparkles, CheckCircle, FileText, Loader2, UploadCloud, Download } from "lucide-react";
import FormattedText from "../../components/FormattedText";
import CsvUploadModal from "../../components/admin/CsvUploadModal";
import { toCsv, downloadCsv } from "../../utils/csvHelper";
import { useToast } from "../../hooks/use-toast";

const PolicyContentEditor = ({
  privacyContent,
  termsContent,
  onSavePrivacy,
  onSaveTerms,
}) => {
  const { toast } = useToast();
  const [activePolicy, setActivePolicy] = useState("privacy"); // "privacy" or "terms"
  const [activeView, setActiveView] = useState("edit"); // "edit" or "preview"
  const [saving, setSaving] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  // Privacy State
  const [privacyForm, setPrivacyForm] = useState({
    title: privacyContent?.title || "Privacy Policy",
    lastUpdated: privacyContent?.lastUpdated || "September 2026",
    contactEmail: privacyContent?.contactEmail || "privacy@minigolfalabama.com",
    introText: (privacyContent?.introParagraphs || []).join("\n\n"),
    sections: privacyContent?.sections || [],
  });

  // Terms State
  const [termsForm, setTermsForm] = useState({
    title: termsContent?.title || "Terms & Conditions",
    lastUpdated: termsContent?.lastUpdated || "September 2026",
    contactEmail: termsContent?.contactEmail || "support@minigolfalabama.com",
    introText: (termsContent?.introParagraphs || []).join("\n\n"),
    sections: termsContent?.sections || [],
  });

  const handlePrivacySectionChange = (index, field, value) => {
    const updated = [...privacyForm.sections];
    updated[index] = { ...updated[index], [field]: value };
    setPrivacyForm({ ...privacyForm, sections: updated });
  };

  const handleAddPrivacySection = () => {
    setPrivacyForm({
      ...privacyForm,
      sections: [
        ...privacyForm.sections,
        {
          title: `${privacyForm.sections.length + 1}. New Policy Section`,
          content: "Describe the policy terms or guidelines here...",
        },
      ],
    });
  };

  const handleRemovePrivacySection = (index) => {
    setPrivacyForm({
      ...privacyForm,
      sections: privacyForm.sections.filter((_, i) => i !== index),
    });
  };

  const handleTermsSectionChange = (index, field, value) => {
    const updated = [...termsForm.sections];
    updated[index] = { ...updated[index], [field]: value };
    setTermsForm({ ...termsForm, sections: updated });
  };

  const handleAddTermsSection = () => {
    setTermsForm({
      ...termsForm,
      sections: [
        ...termsForm.sections,
        {
          title: `${termsForm.sections.length + 1}. New Terms Clause`,
          content: "Enter the legal terms and restrictions here...",
        },
      ],
    });
  };

  const handleRemoveTermsSection = (index) => {
    setTermsForm({
      ...termsForm,
      sections: termsForm.sections.filter((_, i) => i !== index),
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (activePolicy === "privacy") {
        await onSavePrivacy({
          ...privacyContent,
          title: privacyForm.title.trim(),
          lastUpdated: privacyForm.lastUpdated.trim(),
          contactEmail: privacyForm.contactEmail.trim(),
          introParagraphs: privacyForm.introText.split(/\n\n+/).map((p) => p.trim()).filter(Boolean),
          sections: privacyForm.sections,
        });
      } else {
        await onSaveTerms({
          ...termsContent,
          title: termsForm.title.trim(),
          lastUpdated: termsForm.lastUpdated.trim(),
          contactEmail: termsForm.contactEmail.trim(),
          introParagraphs: termsForm.introText.split(/\n\n+/).map((p) => p.trim()).filter(Boolean),
          sections: termsForm.sections,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const isPrivacy = activePolicy === "privacy";
  const currentForm = isPrivacy ? privacyForm : termsForm;

  return (
    <div className="space-y-6">
      {/* Header & Policy Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
            Legal & Policy Content
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Customize Privacy Policy and Terms & Conditions pages across the entire website
          </p>
        </div>

        {/* Policy Selector Tabs */}
        <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => setActivePolicy("privacy")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activePolicy === "privacy"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Shield className="w-4 h-4 text-[#e5243b]" /> Privacy Policy
          </button>
          <button
            type="button"
            onClick={() => setActivePolicy("terms")}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activePolicy === "terms"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Scale className="w-4 h-4 text-[#2563eb]" /> Terms & Conditions
          </button>
        </div>
      </div>

      {/* Editor Main Card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Editor Sub-Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/70 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold ${
              isPrivacy ? "bg-red-100 text-[#e5243b]" : "bg-blue-100 text-[#2563eb]"
            }`}>
              {isPrivacy ? <Shield className="w-5 h-5" /> : <Scale className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Editing: {isPrivacy ? "Privacy Policy Page" : "Terms & Conditions Page"}
              </h2>
              <p className="text-xs text-gray-500">
                All changes reflect instantly on {isPrivacy ? "/privacy-policy" : "/terms-and-conditions"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-gray-200/80 p-0.5 rounded-lg text-xs font-semibold">
              <button
                type="button"
                onClick={() => setActiveView("edit")}
                className={`px-3 py-1.5 rounded-md transition-all ${
                  activeView === "edit" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Form Editor
              </button>
              <button
                type="button"
                onClick={() => setActiveView("preview")}
                className={`px-3 py-1.5 rounded-md transition-all ${
                  activeView === "preview" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Live Preview
              </button>
            </div>

            {/* CSV Actions */}
            <button
              type="button"
              onClick={handleExportCsv}
              title="Export sections to CSV"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button
              type="button"
              onClick={() => setCsvModalOpen(true)}
              title="Import sections from Excel, CSV, TSV, or JSON"
              className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-xl shadow-sm transition-all"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save {isPrivacy ? "Privacy Policy" : "Terms & Conditions"}
            </button>
          </div>
        </div>

        {/* Editor Body */}
        <div className="p-6">
          {activeView === "edit" ? (
            <form onSubmit={handleSave} className="space-y-6">
              {/* Header Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Page Headline (H1)
                  </label>
                  <input
                    type="text"
                    value={currentForm.title}
                    onChange={(e) => {
                      if (isPrivacy) setPrivacyForm({ ...privacyForm, title: e.target.value });
                      else setTermsForm({ ...termsForm, title: e.target.value });
                    }}
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Last Updated Date
                  </label>
                  <input
                    type="text"
                    value={currentForm.lastUpdated}
                    onChange={(e) => {
                      if (isPrivacy) setPrivacyForm({ ...privacyForm, lastUpdated: e.target.value });
                      else setTermsForm({ ...termsForm, lastUpdated: e.target.value });
                    }}
                    placeholder="e.g. September 2026"
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    Contact / Inquiries Email
                  </label>
                  <input
                    type="email"
                    value={currentForm.contactEmail}
                    onChange={(e) => {
                      if (isPrivacy) setPrivacyForm({ ...privacyForm, contactEmail: e.target.value });
                      else setTermsForm({ ...termsForm, contactEmail: e.target.value });
                    }}
                    className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
              </div>

              {/* Introduction Paragraphs */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Introduction Paragraphs (Separate with blank line)
                </label>
                <textarea
                  rows={4}
                  value={currentForm.introText}
                  onChange={(e) => {
                    if (isPrivacy) setPrivacyForm({ ...privacyForm, introText: e.target.value });
                    else setTermsForm({ ...termsForm, introText: e.target.value });
                  }}
                  placeholder="Welcome to our directory..."
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed font-mono text-xs"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Hyperlinks supported: <code className="bg-gray-100 px-1 py-0.5 rounded text-[#e5243b]">[Link Text](https://example.com)</code>
                </p>
              </div>

              {/* Sections List */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Policy Sections ({currentForm.sections.length})
                    </h3>
                    <p className="text-xs text-gray-500">
                      Add, reorder, or update individual policy clauses and descriptions
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={isPrivacy ? handleAddPrivacySection : handleAddTermsSection}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Section
                  </button>
                </div>

                <div className="space-y-4">
                  {currentForm.sections.map((sec, idx) => (
                    <div key={idx} className="bg-gray-50/80 border border-gray-200 rounded-xl p-4 relative space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <label className="block text-[11px] font-bold uppercase text-gray-500 mb-1">
                            Section {idx + 1} Heading
                          </label>
                          <input
                            type="text"
                            value={sec.title}
                            onChange={(e) => {
                              if (isPrivacy) handlePrivacySectionChange(idx, "title", e.target.value);
                              else handleTermsSectionChange(idx, "title", e.target.value);
                            }}
                            className="w-full px-3 py-2 text-sm font-bold border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="e.g. 1. Information We Collect"
                            required
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (isPrivacy) handleRemovePrivacySection(idx);
                            else handleRemoveTermsSection(idx);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-4"
                          title="Delete section"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold uppercase text-gray-500 mb-1">
                          Section Content
                        </label>
                        <textarea
                          rows={3}
                          value={sec.content}
                          onChange={(e) => {
                            if (isPrivacy) handlePrivacySectionChange(idx, "content", e.target.value);
                            else handleTermsSectionChange(idx, "content", e.target.value);
                          }}
                          className="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed"
                          placeholder="Describe the details of this policy clause..."
                          required
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t border-gray-200 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 rounded-xl shadow-md transition-all active:scale-[0.98]"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save All {isPrivacy ? "Privacy Policy" : "Terms & Conditions"} Changes
                </button>
              </div>
            </form>
          ) : (
            /* Live Preview */
            <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 space-y-6">
              <div className="border-b border-gray-100 pb-4">
                <span className="text-xs font-bold uppercase text-[#e5243b]">Live Page Preview</span>
                <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mt-1">
                  {currentForm.title}
                </h1>
                <p className="text-xs text-gray-500 mt-1">
                  Last updated: <strong>{currentForm.lastUpdated}</strong> · Contact: <strong>{currentForm.contactEmail}</strong>
                </p>
              </div>

              <div className="space-y-4">
                {currentForm.introText.split(/\n\n+/).filter(Boolean).map((p, i) => (
                  <FormattedText key={i} text={p} paragraphClassName="text-sm text-gray-700 leading-relaxed" />
                ))}
              </div>

              <div className="space-y-6 pt-4">
                {currentForm.sections.map((sec, i) => (
                  <div key={i} className="space-y-2">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" /> {sec.title}
                    </h3>
                    <FormattedText text={sec.content} paragraphClassName="text-sm text-gray-600 leading-relaxed" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <CsvUploadModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        sectionKey="policies"
        onImport={handleImportCsv}
        allowModeSwitch={true}
        customTitle={`CSV Upload: ${isPrivacy ? "Privacy Policy" : "Terms & Conditions"} Sections`}
      />
    </div>
  );
};

export default PolicyContentEditor;
